import {
  createBigDecimal,
  greaterThan,
  minus,
  toStringBigDecimal,
} from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { hyperliquidAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { parseHyperliquidActionEvidence } from '../parseActionEvidence'
import { type LedgerUpdate, responseSchema } from './schema'

/** Action の transfer が扱うアセット */
type TransferAsset = Action['transfers'][number]['asset']

/** delta の手数料から fee Action を作成する
 * @param asset - 手数料アセット
 * @param configType - API 設定の type (source 生成用)
 * @param item - 対象の更新履歴
 * @param order - 同一 source 内での order (source + order は一意である必要がある)
 * @returns fee Action / 手数料が未定義または 0 の場合は undefined
 */
const createFeeAction = ({
  asset,
  configType,
  item,
  order,
}: {
  asset: TransferAsset
  configType: string
  item: LedgerUpdate
  order: number
}): Action | undefined => {
  if (item.delta.fee === undefined || item.delta.fee.value === 0n) {
    return undefined
  }
  if (item.delta.fee.value < 0n) {
    throw new Error(`Negative fee not allowed: ${item.delta.fee.value}`)
  }
  return {
    comment: `Fee of ${item.delta.type} | ${toStringBigDecimal(item.delta.amount ?? item.delta.usdc ?? createBigDecimal(0n))} ${item.delta.token ?? 'USDC'}${item.delta.user && item.delta.destination ? ` (${item.delta.user} -> ${item.delta.destination})` : ''}`,
    evidence: 'system-rule',
    order,
    source: `${hyperliquidAccountCategory}_${configType}_${item.hash}`,
    type: 'fee',
    timestamp: item.time,
    transfers: [
      {
        asset,
        amount: item.delta.fee,
        direction: 'out',
      },
    ],
  }
}

/** Earn (borrow/lend) の借入・返済を結び付ける loanId を作成する
 * @param symbol - 借入アセットのシンボル
 * @returns アセットごとに一意な loanId
 */
const createBorrowLendLoanId = ({ symbol }: { symbol: string }): string =>
  `${hyperliquidAccountCategory}_borrowLend_${symbol}`

export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'POST',
    path: '/info',
    body: {
      type: 'userNonFundingLedgerUpdates',
      startTime: from,
      ...(to ? { endTime: to } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by time
  const sortedFundings = response.sort((a, b) => a.time - b.time)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedFundings) {
    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? item.time : Math.min(fetchedFrom, item.time)
    fetchedTo =
      fetchedTo === undefined ? item.time : Math.max(fetchedTo, item.time)

    let symbol = 'USDC'
    if (item.delta.token) {
      symbol = item.delta.token
    }
    if (item.delta.feeToken) {
      symbol = item.delta.feeToken
    }
    const asset = getAsset(symbol)
    if (!asset) {
      throw new AssetNotFoundException(symbol)
    }

    switch (item.delta.type) {
      // airdrop
      case 'spotGenesis': {
        if (
          !item.delta.token ||
          !item.delta.amount ||
          item.delta.amount.value < 0n
        ) {
          throw new Error(
            `Invalid spotGenesis delta: ${JSON.stringify(item.delta)}`,
          )
        }
        if (item.delta.amount.value === 0n) {
          break
        }
        actions.push({
          comment: item.delta.type,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'airdrop',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: item.delta.amount,
              direction: 'in',
            },
          ],
        })
        break
      }
      // gift
      case 'rewardsClaim':
      case 'vaultDistribution':
      case 'vaultLeaderCommission': {
        const rewardAmount = item.delta.amount ?? item.delta.usdc
        if (!rewardAmount || rewardAmount.value < 0n) {
          throw new Error(`Invalid delta: ${JSON.stringify(item.delta)}`)
        }
        if (rewardAmount.value === 0n) {
          break
        }
        actions.push({
          comment: item.delta.type,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'gift',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: rewardAmount,
              direction: 'in',
            },
          ],
        })
        break
      }
      // vault
      case 'vaultWithdraw': {
        if (item.delta.commission && item.delta.commission.value > 0n) {
          actions.push({
            comment: `Commision of ${item.delta.type}${item.delta.vault ? ` | Vault: ${item.delta.vault}` : ''}`,
            evidence: 'system-rule',
            order: 0,
            source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
            type: 'gift',
            timestamp: item.time,
            transfers: [
              {
                asset,
                amount: item.delta.commission,
                direction: 'in',
              },
            ],
          })
        }
        if (item.delta.closingCost && item.delta.closingCost.value > 0n) {
          actions.push({
            comment: `Closing Cost of ${item.delta.type}${item.delta.vault ? ` | Vault: ${item.delta.vault}` : ''}`,
            evidence: 'system-rule',
            order: 0,
            source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
            type: 'fee',
            timestamp: item.time,
            transfers: [
              {
                asset,
                amount: item.delta.closingCost,
                direction: 'out',
              },
            ],
          })
        }
        break
      }
      // Earn (borrow/lend) への供給・引き出し・借入・返済
      // - supply: trading -> earn の移動なので stake
      // - withdraw: earn -> trading の移動なので unstake
      // - 引き出し額には利息が含まれるが内訳が確定できないため evidence = 'none' としてユーザー確認を促す
      // - borrow: earn から trading への借入なので borrow
      // - repay: 借入の返済なので repayment (+ 利息分は interest-fee)
      case 'borrowLend': {
        let order = 0
        const operation = item.delta.operation
        const amount = item.delta.amount
        const loanId = createBorrowLendLoanId({ symbol })
        const source = `${hyperliquidAccountCategory}_${config.type}_${item.hash}`
        if (
          operation === 'supply' ||
          operation === 'withdraw' ||
          operation === 'borrow' ||
          operation === 'repay'
        ) {
          if (amount === undefined) {
            // delta は BigInt を含むため JSON.stringify できない
            throw new Error(
              `Invalid borrowLend delta: amount is undefined (operation=${operation}, hash=${item.hash})`,
            )
          }
          if (amount.value < 0n) {
            throw new Error(
              `Negative borrowLend amount not allowed: ${amount.value}`,
            )
          }
        }
        if (
          (operation === 'supply' || operation === 'withdraw') &&
          amount !== undefined
        ) {
          if (amount.value > 0n) {
            const isSupply = operation === 'supply'
            actions.push({
              comment: `${item.delta.type} ${operation}${item.delta.interestAmount && item.delta.interestAmount.value > 0n ? ` | Interest: ${toStringBigDecimal(item.delta.interestAmount)} ${item.delta.token ?? 'USDC'}` : ''}`,
              evidence: 'none',
              order,
              source,
              type: isSupply ? 'stake' : 'unstake',
              timestamp: item.time,
              transfers: [
                {
                  asset,
                  amount,
                  direction: isSupply ? 'out' : 'in',
                },
              ],
            })
            order += 1
          }
        }
        if (operation === 'borrow' && amount !== undefined) {
          if (amount.value > 0n) {
            actions.push({
              comment: `${item.delta.type} ${operation}`,
              evidence: 'system-rule',
              loanId,
              order,
              source,
              type: 'borrow',
              timestamp: item.time,
              transfers: [
                {
                  asset,
                  amount,
                  direction: 'in',
                },
              ],
            })
            order += 1
          }
        }
        // 返済額には利息が含まれる想定 (withdraw の amount に interestAmount が含まれるのと同じ扱い)
        // - amount - interestAmount を repayment、interestAmount を interest-fee とする
        // - 実データで未検証のため evidence = 'none' としてユーザー確認を促す
        if (operation === 'repay' && amount !== undefined) {
          const interestAmount = item.delta.interestAmount
          const interest =
            interestAmount === undefined || interestAmount.value <= 0n
              ? undefined
              : greaterThan(interestAmount, amount)
                ? amount
                : interestAmount
          const principal =
            interest === undefined ? amount : minus(amount, interest)
          if (principal.value > 0n) {
            actions.push({
              comment: `${item.delta.type} ${operation} | Amount: ${toStringBigDecimal(amount)} ${symbol}`,
              evidence: 'none',
              loanId,
              order,
              source,
              type: 'repayment',
              timestamp: item.time,
              transfers: [
                {
                  asset,
                  amount: principal,
                  direction: 'out',
                },
              ],
            })
            order += 1
          }
          if (interest !== undefined && interest.value > 0n) {
            actions.push({
              comment: `${item.delta.type} ${operation} interest`,
              evidence: 'none',
              loanId,
              order,
              source,
              type: 'interest-fee',
              timestamp: item.time,
              transfers: [
                {
                  asset,
                  amount: interest,
                  direction: 'out',
                },
              ],
            })
            order += 1
          }
        }
        const feeAction = createFeeAction({
          asset,
          configType: config.type,
          item,
          order,
        })
        if (feeAction) {
          actions.push(feeAction)
        }
        break
      }
      // 新規アカウントへの初回送金時に送金元が支払う有効化手数料 (1 quote token)
      case 'accountActivationGas': {
        if (item.delta.amount === undefined || item.delta.amount.value < 0n) {
          throw new Error(
            `Invalid accountActivationGas delta: amount=${item.delta.amount ? toStringBigDecimal(item.delta.amount) : 'undefined'} (hash=${item.hash})`,
          )
        }
        if (item.delta.amount.value === 0n) {
          break
        }
        actions.push({
          comment: item.delta.type,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'fee',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: item.delta.amount,
              direction: 'out',
            },
          ],
        })
        break
      }
      // fee
      case 'accountClassTransfer':
      case 'activateDexAbstraction':
      case 'cStakingTransfer':
      case 'deposit':
      case 'internalTransfer':
      case 'send':
      case 'spotTransfer':
      case 'subAccountTransfer':
      case 'vaultCreate':
      case 'vaultDeposit':
      case 'withdraw': {
        const feeAction = createFeeAction({
          asset,
          configType: config.type,
          item,
          order: 0,
        })
        if (feeAction) {
          actions.push(feeAction)
        }
        break
      }
      default: {
        throw new Error(`Unsupported delta type: ${item.delta.type}`)
      }
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions.map((action) =>
      parseHyperliquidActionEvidence(action),
    ),
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
